<template>
  <div v-if="visible" class="add-task-popup-overlay">
    <div class="add-task-popup">
      <div class="add-task-popup-header">
        <h2>Afspraak toevoegen</h2>
        <X :stroke-width="1" class="close-icon" @click="$emit('close')" />
      </div>
      <form @submit.prevent="submitTask">
        <div class="form-block">
          <p class="form-block-title">Klant</p>
          <div class="form-grid">
            <div class="form-section client-search-section">
              <label>Klant</label>
              <div class="input-icon-wrapper input-icon-right">
                <input
                  type="text"
                  v-model="searchClient"
                  placeholder="Zoek klant..."
                  @input="onClientInput"
                  @focus="onClientFocus"
                  @keydown.down.prevent="moveClientSelection(1)"
                  @keydown.up.prevent="moveClientSelection(-1)"
                  @keydown.enter.prevent="selectHighlightedClient"
                  @blur="onClientBlur"
                />
                <Search :stroke-width="1" class="input-icon" />
              </div>
              <ul
                v-if="showClientSuggestions"
                class="prospectie-suggestions-dropdown"
              >
                <li
                  v-for="(client, idx) in filteredClientResults"
                  :key="client.id"
                  :class="[
                    'suggestion-item',
                    { active: idx === activeClientIndex },
                  ]"
                  @mousedown.prevent="selectClient(client)"
                >
                  <span class="suggestion-text">{{ client.name }}</span>
                </li>
              </ul>
            </div>
            <div class="form-section">
              <label>Deal</label>
              <CustomDropdown
                v-model="selectedDeal"
                :options="[
                  { value: '', label: 'Selecteer deal' },
                  ...loadedDeals.map((deal) => ({
                    value: deal._id || deal.id,
                    label: deal.title || deal.name,
                  })),
                ]"
                placeholder="Selecteer deal"
              />
            </div>
          </div>
        </div>

        <div class="form-block">
          <p class="form-block-title">Beschrijving</p>
          <div class="form-grid">
            <div class="form-section">
              <label>Titel</label>
              <input
                type="text"
                v-model="taskTitle"
                placeholder="Titel taak..."
              />
            </div>
            <div class="form-section">
              <label>Toewijzen aan</label>
              <CustomDropdown
                v-model="responsible"
                :options="[
                  {
                    value: '',
                    label: 'Selecteer verantwoordelijke',
                    avatar: null,
                  },
                  ...loadedUsers.map((user) => ({
                    value: user._id || user.id,
                    label: user.name || user.firstName + ' ' + user.lastName,
                    avatar: user.avatar || null,
                  })),
                ]"
                placeholder="Selecteer verantwoordelijke"
                :hideAvatar="false"
              />
            </div>

            <div class="form-section">
              <label>Datum</label>
              <div
                style="
                  position: relative;
                  display: flex;
                  align-items: center;
                  gap: 0.5em;
                "
              >
                <span
                  style="
                    position: absolute;
                    left: 1em;
                    pointer-events: none;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    height: 100%;
                  "
                >
                  <Calendar
                    :stroke-width="1"
                    style="
                      width: 1.2em;
                      height: 1.2em;
                      color: #fff;
                      opacity: 0.7;
                    "
                  />
                </span>
                <input
                  type="text"
                  class="date-input"
                  :value="formatDueDate(dueDate)"
                  readonly
                  @click="showDueDateCalendar = true"
                  placeholder="Kies een datum"
                  style="
                    padding-left: 3em;
                    cursor: pointer;
                    background: #222;
                    color: #fff;
                    border-radius: 6px;
                    border: 1px solid var(--border, #e5e7eb);
                    padding: 0.5rem 1rem;
                    width: 100%;
                  "
                />
                <button
                  v-if="dueDate"
                  type="button"
                  @click="clearDueDate"
                  style="
                    background: none;
                    border: none;
                    color: #fff;
                    font-size: 1.2em;
                    cursor: pointer;
                  "
                >
                  &times;
                </button>
                <div
                  v-if="showDueDateCalendar"
                  class="calendar-popup"
                  @click.stop
                >
                  <CalendarComponent
                    v-model="dueDate"
                    @update:modelValue="showDueDateCalendar = false"
                    :min-value="null"
                    :max-value="null"
                    layout="month-and-year"
                    class="custom-calendar"
                  />
                </div>
              </div>
            </div>

            <div class="form-section">
              <label>Om</label>
              <div class="input-icon-wrapper">
                <Timer :stroke-width="1" class="input-icon" />
                <input type="time" v-model="time" step="60" />
              </div>
            </div>
            <div class="form-section">
              <label>Duur</label>
              <div class="input-icon-wrapper">
                <Timer :stroke-width="1" class="input-icon" />
                <input type="time" v-model="duration" step="60" />
              </div>
            </div>
            <div class="form-section">
              <label>Online vergaderuimte</label>
              <CustomDropdown
                v-model="onlineRoom"
                :options="[
                  { value: '', label: 'Selecteer vergaderplatform' },
                  { value: 'teams', label: 'Microsoft Teams' },
                  { value: 'zoom', label: 'Zoom' },
                  { value: 'googlemeet', label: 'Google Meet' },
                  { value: 'webex', label: 'Webex' },
                  { value: 'anders', label: 'Anders' },
                ]"
                placeholder="Selecteer vergaderplatform"
              />
            </div>
            <div class="form-section">
              <label>Locatie</label>
              <CustomDropdown
                v-model="location"
                :options="[
                  { value: '', label: 'Selecteer locatie' },
                  { value: 'office', label: 'Op kantoor' },
                  { value: 'company', label: 'Locatie van een bedrijf' },
                  { value: 'person', label: 'Locatie van een contactpersoon' },
                  { value: 'one-time', label: 'Locatie voor eenmalig gebruik' },
                  { value: 'virtual', label: 'Virtuele meeting' },
                ]"
                placeholder="Selecteer locatie"
              />
            </div>
            <div
              v-if="location === 'company' || location === 'person'"
              class="form-section"
            >
              <label>
                {{
                  location === "company"
                    ? "Kies bedrijf"
                    : "Kies contactpersoon"
                }}
              </label>
              <input
                type="text"
                v-model="companyContact"
                :placeholder="
                  location === 'company'
                    ? 'Kies bedrijf'
                    : 'Kies contactpersoon'
                "
              />
            </div>
            <div class="form-section grid-span-2">
              <label>Beschrijving</label>
              <textarea
                v-model="taskDescription"
                placeholder="Beschrijving..."
              />
            </div>
          </div>
        </div>
        <div class="form-actions">
          <AppButton type="submit" variant="primary"
            >Opslaan en inplannen</AppButton
          >
          <AppButton type="submit" variant="primary">Opslaan</AppButton>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/auth.js";
import { X, Search, Timer, Calendar } from "lucide-vue-next";
import ToggleSwitch from "./ToggleSwitch.vue";
import CustomDropdown from "./CustomDropdown.vue";
import AppButton from "./AppButton.vue";
import CalendarComponent from "@/components/ui/calendar/Calendar.vue";
import { useRouter } from "vue-router";

export default {
  name: "AddAppointmentPopup",
  components: {
    X,
    Search,
    Timer,
    ToggleSwitch,
    CustomDropdown,
    AppButton,
    Calendar,
    CalendarComponent,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projects: Array,
    deals: Array,
    users: Array,
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchClient: "",
      clientResults: [],
      selectedClient: null,
      selectedProject: "",
      selectedDeal: "",
      taskType: "appointment",
      taskTitle: "",
      taskDescription: "",
      responsible: "",
      dueDate: new Date(),
      showDueDateCalendar: false,
      priority: "",
      location: "",
      companyContact: "",
      onlineRoom: "",
      repeat: false,
      time: "09:00", // starttijd
      duration: "00:30", // duur
      showClientSuggestions: false,
      activeClientIndex: -1,
      loadedUsers: [], // Users fetched from API
      loadedProjects: [], // Projects fetched from API
      loadedDeals: [], // Deals fetched from API
      // deals: [], // deals komen via props
    };
  },
  async created() {
    // Fetch users, projects, and deals from API
    await this.fetchUsers();
    await this.fetchProjects();
    await this.fetchDeals();
  },
  // Geen fetchDeals, deals komen via props
  watch: {
    visible(newVal) {
      if (newVal && this.initialData) {
        this.applyInitialData();
      }
    },
  },
  computed: {
    dealOptions() {
      const opts = [
        { value: "", label: "Selecteer deal" },
        ...(Array.isArray(this.deals)
          ? this.deals.map((deal) => ({
              value: deal._id || deal.id,
              label: deal.title,
            }))
          : []),
      ];
      return opts;
    },
    safeClientResults() {
      return Array.isArray(this.clientResults) ? this.clientResults : [];
    },
    filteredClientResults() {
      if (!Array.isArray(this.clientResults)) return [];
      const term = (this.searchClient || "").trim().toLowerCase();
      if (!term) return this.clientResults;
      return this.clientResults.filter((c) =>
        (c.name || "").toLowerCase().includes(term)
      );
    },
  },
  setup() {
    const router = useRouter();
    return { router };
  },
  methods: {
    async fetchUsers() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/users",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedUsers = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    async fetchProjects() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/projects",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedProjects = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    },
    async fetchDeals() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/deals",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedDeals = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching deals:", error);
      }
    },
    async fetchUsers() {
      try {
        const token = AuthService.token || localStorage.getItem("authToken");
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/users",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.ok) {
          const data = await res.json();
          this.loadedUsers = Array.isArray(data) ? data : data.data || [];
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    applyInitialData() {
      // Apply initial data from draw-to-create feature
      if (!this.initialData) return;

      // Set the date from initialData.date (format: "YYYY-MM-DD")
      if (this.initialData.date) {
        const [year, month, day] = this.initialData.date.split("-").map(Number);
        this.dueDate = new Date(year, month - 1, day);
      }

      // Set the time from initialData.startAt (in seconds from midnight)
      if (this.initialData.startAt !== undefined) {
        const seconds = this.initialData.startAt;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        this.time = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;
      }

      // Set duration from initialData.duration (in seconds)
      if (this.initialData.duration !== undefined) {
        const totalSeconds = this.initialData.duration;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        this.duration = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;
      }
    },
    // Fetch all clients (companies) for the client search dropdown
    async fetchAllClients() {
      const apiUrl =
        "https://netnest-internal-platform-backend-2dow.onrender.com/api/companies";
      const token = AuthService.token;
      try {
        this.clientResults = [];
        this.clientLoading = true;
        const res = await fetch(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        const companies = Array.isArray(data)
          ? data
          : Array.isArray(data.data)
          ? data.data
          : [];
        this.clientResults = companies.map((c) => ({
          id: c._id || c.id,
          name: c.name || c.companyName || c.title || "",
        }));
      } catch (e) {
        this.clientResults = [];
      } finally {
        this.clientLoading = false;
      }
    },
    logAndSubmitAppointment() {
      this.submitTask();
    },
    // --- Client search logic matching DealAdd.vue ---
    async onClientInput() {
      this.showClientSuggestions = true;
      this.activeClientIndex = -1;
      await this.updateClientResults();
    },
    async onClientFocus() {
      this.showClientSuggestions = true;
      this.activeClientIndex = -1;
      if (!this.searchClient || this.searchClient.length < 2) {
        await this.fetchAllClients();
      } else {
        await this.updateClientResults();
      }
    },
    onClientBlur() {
      setTimeout(() => {
        this.showClientSuggestions = false;
      }, 120);
    },
    async updateClientResults() {
      // Real API search
      const query =
        this.searchClient && this.searchClient.length >= 2
          ? this.searchClient
          : "";
      let apiUrl =
        "https://netnest-internal-platform-backend-2dow.onrender.com/api/companies";
      if (query) {
        apiUrl += `?q=${encodeURIComponent(query)}`;
      }
      const token = AuthService.token;
      try {
        this.clientResults = [];
        this.clientLoading = true;
        const res = await fetch(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        const companies = Array.isArray(data)
          ? data
          : Array.isArray(data.data)
          ? data.data
          : [];
        this.clientResults = companies.map((c) => ({
          id: c._id || c.id,
          name: c.name || c.companyName || c.title || "",
        }));
      } catch (e) {
        this.clientResults = [];
      } finally {
        this.clientLoading = false;
      }
    },
    moveClientSelection(dir) {
      if (!this.showClientSuggestions || !this.clientResults.length) return;
      let idx = this.activeClientIndex + dir;
      if (idx < 0) idx = this.clientResults.length - 1;
      if (idx >= this.clientResults.length) idx = 0;
      this.activeClientIndex = idx;
    },
    selectHighlightedClient() {
      if (
        this.showClientSuggestions &&
        this.activeClientIndex >= 0 &&
        this.activeClientIndex < this.clientResults.length
      ) {
        this.selectClient(this.clientResults[this.activeClientIndex]);
      }
    },
    selectClient(client) {
      this.selectedClient = client;
      this.searchClient = client.name;
      this.clientResults = [];
      this.showClientSuggestions = false;
      this.activeClientIndex = -1;
    },
    formatDueDate(val) {
      if (!val) return "";
      let dateObj = val;
      if (typeof val === "string" && !isNaN(Date.parse(val))) {
        dateObj = new Date(val);
      } else if (val && typeof val.toDate === "function") {
        dateObj = val.toDate();
      }
      if (dateObj instanceof Date && !isNaN(dateObj)) {
        const d = dateObj.getDate().toString().padStart(2, "0");
        const m = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const y = dateObj.getFullYear();
        return `${d}/${m}/${y}`;
      }
      return "";
    },
    clearDueDate() {
      this.dueDate = "";
    },
    // fetchDeals verwijderd, deals komen via props
    async submitTask(e) {
      // Bepaal of het inplannen is of niet
      let planned = false;
      if (
        e &&
        e.target &&
        e.target.innerText &&
        e.target.innerText.toLowerCase().includes("inplannen")
      ) {
        planned = true;
      }

      // Combine dueDate and time for proper backend storage
      const dateWithTime = new Date(this.dueDate);
      const [hours, minutes] = this.time.split(":");
      dateWithTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      const dueDateISO = dateWithTime.toISOString();

      // Convert duration from "HH:MM" to seconds
      const [durationHours, durationMinutes] = this.duration.split(":");
      const durationSeconds =
        parseInt(durationHours) * 3600 + parseInt(durationMinutes) * 60;

      // Get current user ID for createdBy
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const currentUserId = user._id || user.id;

      // Verzenden naar backend
      const appointmentObj = {
        client: this.selectedClient,
        companyId: this.selectedClient ? this.selectedClient.id : undefined,
        project: this.selectedProject,
        dealId: this.selectedDeal,
        type: "appointment",
        title: this.taskTitle,
        description: this.taskDescription,
        responsible: this.responsible || currentUserId, // Default to current user if not set
        dueDate: dueDateISO,
        date: dueDateISO, // Combine date + time
        duration: durationSeconds, // Send duration in seconds
        priority: this.priority,
        location: this.location,
        locationCompanyName:
          this.location === "company"
            ? this.selectedClient?.name || this.companyContact
            : "",
        locationContactName:
          this.location === "person" ? this.companyContact : "",
        locationCustom: this.location === "one-time" ? this.companyContact : "",
        companyContact: this.companyContact,
        onlineRoom: this.onlineRoom,
        repeat: this.repeat,
        planned,
        createdBy: currentUserId,
      };
      // Alleen meesturen als het een geldige ObjectId is (24 chars)
      if (
        this.location === "company" &&
        this.selectedClient &&
        typeof this.selectedClient.id === "string" &&
        this.selectedClient.id.length === 24
      ) {
        appointmentObj.locationCompanyId = this.selectedClient.id;
      }
      try {
        const token = AuthService.token;
        const res = await fetch(
          "https://netnest-internal-platform-backend-2dow.onrender.com/api/activities",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(appointmentObj),
          }
        );
        const data = await res.json();
        let activity =
          data && data.data && data.data._id ? data.data : appointmentObj;

        // Agenda-entry direct aanmaken
        let projectId =
          activity.project?._id ||
          activity.project ||
          activity.projectId ||
          activity.projectNumber;
        let userId =
          activity.responsible || activity.createdBy || currentUserId;
        let dateObj = new Date(
          activity.date || activity.deliveryDate || dueDateISO
        );
        let dateStr = `${dateObj.getUTCFullYear()}-${String(
          dateObj.getUTCMonth() + 1
        ).padStart(2, "0")}-${String(dateObj.getUTCDate()).padStart(2, "0")}`;
        let startAt =
          dateObj.getUTCHours() * 3600 + dateObj.getUTCMinutes() * 60;
        let taskTitle = activity.title || activity.taskTitle || "Afspraak";
        let duration = activity.duration;
        const agendaPayload = {
          userId,
          projectId,
          date: dateStr,
          task: taskTitle,
          duration,
          startAt,
        };
        try {
          await fetch("/api/Agenda", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(agendaPayload),
          });
        } catch (err) {
          console.warn("Kon agenda-item niet aanmaken:", err);
        }

        this.$emit("appointment-created", activity);
        this.$emit("close");
      } catch (e) {
        console.error("Error creating appointment:", e);
        this.$emit("appointment-created", appointmentObj);
        this.$emit("close");
      }
    },
  },
};
</script>

<style scoped>
/* Calendar popup styling zoals in DealAdd.vue */
.calendar-popup {
  position: absolute;
  top: 2.5em;
  left: 0;
  z-index: 100;
  background: #18181b;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}
.date-input:focus {
  box-shadow: 0 0 0 2px var(--primary_color, #4f46e5);
}
.date-input {
  padding-left: 3em !important;
}
.date-input::placeholder {
  color: #bbb;
  opacity: 1;
}
.form-block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.form-block-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
}
.add-task-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem 0;
}
.add-task-popup {
  background: var(--surface);
  border-radius: 0.75rem;
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.12);
  padding: 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.add-task-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.add-task-popup form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.close-icon {
  width: 1.2rem;
  height: 1.2rem;
  cursor: pointer;
  color: #fff;
  transition: color 0.2s;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
}
.form-section {
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
}
.form-section label {
  margin-bottom: 0.5rem;
}
.form-section input,
.form-section select,
.form-section textarea {
  padding: 0.5rem;
  border-radius: 4px;
}
.form-section textarea {
  min-height: 60px;
}
.single-col {
  margin-top: 0.5rem;
  width: 100%;
}
.single-col input,
.single-col textarea {
  width: 100%;
  max-width: 100%;
}
.search-results {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-top: 0.25rem;
  list-style: none;
  padding: 0;
  max-height: 120px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}
.search-results li {
  padding: 0.5rem 1rem;
  cursor: pointer;
}
.search-results li:hover {
  background: #f3f4f6;
}
.form-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  grid-column: 1 / span 2;
}
.input-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--input-bg, #222);
  border-radius: 0.5rem;
}
.input-icon {
  position: absolute;
  width: 1rem;
  height: 1rem;
  color: #fff;
  pointer-events: none;
  left: 0.5rem;
}
.input-icon-wrapper.input-icon-right .input-icon {
  right: 0.75rem;
  left: auto;
}
.input-icon-wrapper.input-icon-right input {
  padding-left: 0.5rem;
  padding-right: 2rem;
}
.input-icon-wrapper:not(.input-icon-right) input {
  padding-left: 2rem;
}

/* Specifiek voor timer input: padding links voor icon */
.input-icon-wrapper input[type="time"] {
  padding-left: 2rem;
}
.grid-span-2 {
  grid-column: 1 / span 2;
  width: 100%;
  max-width: 100%;
}
/* --- Prospectie-style suggestions dropdown for client search (from DealAdd.vue) --- */
.prospectie-suggestions-dropdown {
  background: #222;
  border: 1px solid #444;
  border-radius: 6px;
  list-style: none;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  z-index: 40;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
}
.prospectie-suggestions-dropdown .suggestion-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
}
.prospectie-suggestions-dropdown .suggestion-item.active,
.prospectie-suggestions-dropdown .suggestion-item:hover {
  background: var(--primary_color, #4f46e5);
  color: #fff;
}
.prospectie-suggestions-dropdown .suggestion-text {
  font-weight: 500;
  color: #fff;
}
/* Zorg dat de client dropdown onder het inputveld staat */
.client-search-section {
  position: relative;
  z-index: 30;
}
</style>
